import { useEffect, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Star } from "lucide-react";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

interface PreguntaPublica {
  id: number;
  tipo: "texto" | "seleccion" | "rating";
  texto: string;
  requerida: boolean;
  opciones?: Array<{ id: number; texto: string }>;
}
interface BrandingSucursal {
  logoUrl?: string | null;
  imagenFondoUrl?: string | null;
  colorPrimario?: string | null;
  colorSecundario?: string | null;
  sucursalNombre?: string | null;
}
interface EncuestaPublica {
  yaRespondida: boolean;
  titulo: string;
  descripcion?: string | null;
  preguntas?: PreguntaPublica[];
  branding?: BrandingSucursal | null;
}

const ACCENT_DEFAULT = "#6B9F2F";

export default function ResponderEncuestaPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<EncuestaPublica | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [respuestas, setRespuestas] = useState<Record<number, { opcionId?: number; texto?: string }>>({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [mostrarRedes, setMostrarRedes] = useState(false);
  const [instagram, setInstagram] = useState("");
  const [twitter, setTwitter] = useState("");
  const [facebook, setFacebook] = useState("");
  const [tiktok, setTiktok] = useState("");

  useEffect(() => {
    fetch(`/api/v1/encuestas/responder/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message ?? "Enlace inválido");
        return r.json();
      })
      .then((d) => {
        setData(d);
        aplicarFaviconPagina(d?.branding?.logoUrl);
        aplicarTituloPagina(d?.branding?.sucursalNombre ?? d?.titulo);
      })
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, [token]);

  async function enviar() {
    setError(null);
    const faltantes = (data?.preguntas ?? []).filter((p) => {
      if (!p.requerida) return false;
      const r = respuestas[p.id];
      return p.tipo === "texto" ? !r?.texto?.trim() : !r?.opcionId;
    });
    if (faltantes.length > 0) {
      setError("Por favor respondé todas las preguntas obligatorias.");
      return;
    }
    setEnviando(true);
    try {
      const body = {
        respuestas: Object.entries(respuestas).map(([preguntaId, v]) => ({
          preguntaId: Number(preguntaId),
          opcionId: v.opcionId,
          texto: v.texto,
        })),
        instagram: instagram.trim() || undefined,
        twitter: twitter.trim() || undefined,
        facebook: facebook.trim() || undefined,
        tiktok: tiktok.trim() || undefined,
      };
      const r = await fetch(`/api/v1/encuestas/responder/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message ?? "No pudimos enviar tu respuesta");
      setEnviado(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  }

  const accent = data?.branding?.colorPrimario || ACCENT_DEFAULT;
  const fondoEstilo: CSSProperties = data?.branding?.imagenFondoUrl
    ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${data.branding.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  if (cargando) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="size-6 animate-spin text-brand-500" /></div>;
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-ink-50">
        <div className="card max-w-sm w-full p-6 text-center">
          <p className="text-sm text-ink-600">{error}</p>
        </div>
      </div>
    );
  }

  if (enviado || data?.yaRespondida) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-ink-50" style={fondoEstilo}>
        <div className="card max-w-sm w-full p-8 text-center">
          <CheckCircle2 className="size-12 text-emerald-500 mx-auto mb-3" />
          <h1 className="font-display font-bold text-lg text-ink-800 mb-1">¡Gracias!</h1>
          <p className="text-sm text-ink-500">
            {enviado ? "Tu respuesta quedó registrada." : "Esta encuesta ya fue respondida anteriormente."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 py-10 px-4" style={fondoEstilo}>
      <div className="max-w-lg mx-auto card p-6 space-y-5">
        <header className="text-center">
          {data?.branding?.logoUrl ? (
            <img src={data.branding.logoUrl} alt="" className="size-14 rounded-full object-cover mx-auto mb-2 border-2" style={{ borderColor: accent }} />
          ) : (
            <Star className="size-8 mx-auto mb-2" style={{ color: accent }} />
          )}
          <h1 className="font-display font-bold text-xl text-ink-800">{data?.titulo}</h1>
          {data?.descripcion && <p className="text-sm text-ink-500 mt-1">{data.descripcion}</p>}
        </header>

        {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}

        <div className="space-y-4">
          {data?.preguntas?.map((p, idx) => (
            <div key={p.id}>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">
                {idx + 1}. {p.texto} {p.requerida && <span className="text-red-500">*</span>}
              </label>
              {p.tipo === "texto" ? (
                <textarea className="input w-full text-base" rows={6} style={{ minHeight: "9rem" }}
                  value={respuestas[p.id]?.texto ?? ""}
                  onChange={(e) => setRespuestas((r) => ({ ...r, [p.id]: { texto: e.target.value } }))} />
              ) : p.tipo === "rating" ? (
                <div className="flex flex-wrap gap-2">
                  {p.opciones?.map((o, i) => {
                    const sel = respuestas[p.id]?.opcionId === o.id;
                    return (
                      <label key={o.id}
                        className="flex-1 min-w-[5.5rem] flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border cursor-pointer text-xs font-medium text-center transition-colors border-ink-200 hover:bg-ink-50 text-ink-600"
                        style={sel ? { borderColor: accent, backgroundColor: `${accent}1a`, color: accent } : undefined}>
                        <input type="radio" name={`pregunta-${p.id}`} className="sr-only"
                          checked={sel}
                          onChange={() => setRespuestas((r) => ({ ...r, [p.id]: { opcionId: o.id } }))} />
                        <span className="text-base leading-none">{i + 1}</span>
                        {o.texto}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {p.opciones?.map((o) => {
                    const sel = respuestas[p.id]?.opcionId === o.id;
                    return (
                      <label key={o.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors border-ink-200 hover:bg-ink-50"
                        style={sel ? { borderColor: accent, backgroundColor: `${accent}1a`, color: accent } : undefined}>
                        <input type="radio" name={`pregunta-${p.id}`} style={{ accentColor: accent }}
                          checked={sel}
                          onChange={() => setRespuestas((r) => ({ ...r, [p.id]: { opcionId: o.id } }))} />
                        {o.texto}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {!mostrarRedes ? (
          <button type="button" onClick={() => setMostrarRedes(true)} className="text-xs font-medium hover:underline" style={{ color: accent }}>
            + Agregar mis redes sociales (opcional)
          </button>
        ) : (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <input className="input text-sm" placeholder="Instagram @usuario" value={instagram} onChange={(e) => setInstagram(e.target.value)} />
            <input className="input text-sm" placeholder="X @usuario" value={twitter} onChange={(e) => setTwitter(e.target.value)} />
            <input className="input text-sm" placeholder="Facebook" value={facebook} onChange={(e) => setFacebook(e.target.value)} />
            <input className="input text-sm" placeholder="TikTok @usuario" value={tiktok} onChange={(e) => setTiktok(e.target.value)} />
          </div>
        )}

        <button onClick={enviar} disabled={enviando} style={{ backgroundColor: accent }}
          className="w-full py-3 rounded-lg hover:opacity-90 text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {enviando && <Loader2 className="size-4 animate-spin" />}
          Enviar respuestas
        </button>
      </div>
    </div>
  );
}
